import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cx } from "./cx";

// Pagination — page is 1-based. onChange(nextPage). Renders up to 7 page
// buttons with ellipses for large ranges.
//
// REFINEMENT NOTES:
// - Every button carries a 1px border (transparent on the active/arrow buttons)
//   so active and inactive pages are the same size and the row never jitters.
// - tabular-nums so page numbers don't shift width.
// - Disabled arrows no longer show a hover background.
function pageList(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

export function Pagination({ page, pageCount, onChange, className = "" }) {
  if (!pageCount || pageCount < 1) return null;
  const go = (p) => {
    const next = Math.min(Math.max(1, p), pageCount);
    if (next !== page) onChange?.(next);
  };
  const btn =
    "inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-btn border text-sm font-medium font-mono tabular-nums transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

  return (
    <nav className={cx("inline-flex items-center gap-1", className)} aria-label="Pagination">
      <button
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className={cx(
          btn,
          "border-transparent text-body hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        )}
      >
        <ChevronLeft size={16} />
      </button>
      {pageList(page, pageCount).map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} className="px-2 text-body/50 select-none">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => go(p)}
            aria-current={p === page ? "page" : undefined}
            className={cx(
              btn,
              p === page
                ? "border-transparent bg-primary text-white"
                : "border-border text-body hover:bg-surface"
            )}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => go(page + 1)}
        disabled={page >= pageCount}
        aria-label="Next page"
        className={cx(
          btn,
          "border-transparent text-body hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        )}
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
}
